// src/components/Talleres/FormularioInscripcion/PasoDatos.jsx
import React, { useEffect, useState } from 'react';
import {
  Alert, Card, Radio, Space, Descriptions, Tag, Spin, Empty, Badge, Tooltip,
  Button, Divider
} from 'antd';
import {
  UserOutlined, TeamOutlined, PlusOutlined,
  WarningOutlined, CheckCircleOutlined, IdcardOutlined, LockOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import Inscripciones from '../../../service/Inscripciones';

const getCategoriaEdad = (calcularEdad, fechaNacimiento) => {
  const edad = calcularEdad(fechaNacimiento);
  if (edad === null) return { texto: 'Sin edad', color: 'gray' };
  if (edad < 18) return { texto: 'Menor', color: 'blue' };
  else if (edad >= 60) return { texto: 'Adulto Mayor', color: 'purple' };
  else return { texto: 'Adulto', color: 'blue' };
};

export default function PasoDatos({
  user,
  familiares,
  loadingFamiliares,
  tipoInscripcion,
  setTipoInscripcion,
  selectedFamiliar,
  setSelectedFamiliar,
  taller,
  selectedHorarios,
  validarRangoEdad,
  calcularEdad,
  tarjetaVecino,
  tipoAcceso,
  familiaresAutorizados,
  onNext,
  onPrev
}) {
  const navigate = useNavigate();
  const { validarRut, validando, estadoVecino, esVecino, resetEstado } = tarjetaVecino;

  const revalidar = async () => {
    let rutAValidar = null;
    if (tipoInscripcion === 'para_mi' && user?.rut) {
      rutAValidar = user.rut;
    } else if (tipoInscripcion === 'para_familiar' && selectedFamiliar) {
      const familiarSeleccionado = familiares.find(f => f._id === selectedFamiliar);
      if (familiarSeleccionado?.rut) rutAValidar = familiarSeleccionado.rut;
    }
    if (rutAValidar) {
      resetEstado();
      await validarRut(rutAValidar);
    }
  };

  // Determinar si puede inscribirse a sí mismo
  const puedeInscribirseSiMismo = taller?.tipoTaller !== 'academia' || tipoAcceso === 'usuario';

  // Filtrar solo familiares autorizados si es academia
  const familiaresDisponibles = React.useMemo(() => {
    if (taller?.tipoTaller !== 'academia' || tipoAcceso === 'usuario') {
      return familiares;
    }

    if (tipoAcceso === 'familiar' && familiaresAutorizados?.length > 0) {
      return familiares.filter(f => familiaresAutorizados.includes(f._id));
    }

    return [];
  }, [familiares, taller?.tipoTaller, tipoAcceso, familiaresAutorizados]);

  // Auto-seleccionar "para_familiar" si no puede inscribirse a sí mismo
  useEffect(() => {
    if (!puedeInscribirseSiMismo && tipoInscripcion === 'para_mi') {
      setTipoInscripcion('para_familiar');
      setSelectedFamiliar(null);
    }
  }, [puedeInscribirseSiMismo, tipoInscripcion, setTipoInscripcion, setSelectedFamiliar]);

  // Validar SIEMPRE el RUT de la persona a inscribir (titular o familiar)
  useEffect(() => {
    const validarAutomatico = async () => {
      resetEstado();

      let rutAValidar = null;

      if (tipoInscripcion === 'para_mi' && user?.rut) {
        rutAValidar = user.rut;
      }

      if (tipoInscripcion === 'para_familiar' && selectedFamiliar) {
        const familiarSeleccionado = familiares.find(f => f._id === selectedFamiliar);
        if (familiarSeleccionado?.rut) {
          rutAValidar = familiarSeleccionado.rut;
        }
      }

      if (rutAValidar) {
        await validarRut(rutAValidar);
      }
    };

    validarAutomatico();
  }, [tipoInscripcion, selectedFamiliar, user?.rut, familiares, resetEstado, validarRut]);

  // ── ¿La persona elegida YA está inscrita en alguno de estos horarios?
  //
  // El carrito valida lo mismo, pero al AGREGAR — y acá el participante todavía no
  // estaba elegido. Sin este aviso el socio se entera al pagar, o no se entera y paga
  // dos veces el mismo cupo (Renata Arenas, 19-08-2026: 3 cobros por un horario).
  // Cubre también al niño que ya inscribió el otro apoderado desde su cuenta: la
  // consulta va por RUT + horario, no por usuario.
  const [yaInscritoEn, setYaInscritoEn] = useState([]);
  const [verificandoInscripcion, setVerificandoInscripcion] = useState(false);

  const participante = React.useMemo(() => {
    if (tipoInscripcion === 'para_mi') return { rut: user?.rut || null, nombre: user?.name || 'Esta persona' };
    if (tipoInscripcion === 'para_familiar' && selectedFamiliar) {
      const f = familiares.find(x => x._id === selectedFamiliar);
      return { rut: f?.rut || null, nombre: f?.nombre || 'Esta persona' };
    }
    return { rut: null, nombre: 'Esta persona' };
  }, [tipoInscripcion, selectedFamiliar, user?.rut, user?.name, familiares]);

  // Los ids como string: `selectedHorarios` es un array nuevo en cada render del
  // padre, así que usarlo de dependencia directa dispara el efecto sin parar.
  const horariosKey = (selectedHorarios || []).map(h => h._id).join(',');

  useEffect(() => {
    let cancelado = false;

    const verificar = async () => {
      const horarios = selectedHorarios || [];
      if (!participante.rut || horarios.length === 0) {
        setYaInscritoEn([]);
        return;
      }
      setVerificandoInscripcion(true);
      try {
        const resultados = await Promise.all(
          horarios.map(h =>
            Inscripciones.VerificarInscripcion(h._id, participante.rut)
              .then(r => ({ horario: h, ...(r?.data || {}) }))
              // Si la consulta falla (red, sesión), no se bloquea la compra: esto es
              // un aviso temprano, y el checkout ya no deja que el cobro se duplique.
              .catch(() => null)
          )
        );
        if (!cancelado) setYaInscritoEn(resultados.filter(r => r?.inscrito));
      } finally {
        if (!cancelado) setVerificandoInscripcion(false);
      }
    };

    verificar();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participante.rut, horariosKey]);

  const nombreHorario = (h) => {
    const bloque = h?.bloques?.[0];
    const dias = (h?.bloques || []).flatMap(b => b.dias || []).join('/');
    return [dias, bloque?.inicio && `${bloque.inicio} h.`].filter(Boolean).join(' ') || 'este horario';
  };

  return (
    <div>
      <Alert
        message="¿Para quién es la inscripción?"
        type="info"
        showIcon
        className="mb-4"
      />

      {/* Alerta: Si solo tiene acceso por familiares */}
      {taller?.tipoTaller === 'academia' && tipoAcceso === 'familiar' && (
        <Alert
          message="Academia con acceso restringido"
          description={
            <div>
              <p>
                <LockOutlined className="mr-2" />
                Solo puedes inscribir a tus <strong>familiares autorizados</strong> en esta academia.
              </p>
              <p className="text-xs mt-1">
                {familiaresAutorizados?.length || 0} familiar(es) autorizado(s)
              </p>
            </div>
          }
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      {validando && (
        <Alert
          message="Validando tarjeta vecino..."
          description="Consultando el sistema de tarjeta vecino..."
          type="info"
          showIcon
          icon={<Spin size="small" />}
          className="mb-4"
        />
      )}

      {estadoVecino && (
        <Alert
          message={
            <div className="flex items-center gap-2">
              <IdcardOutlined />
              <span className="font-semibold">
                {estadoVecino.esVecino
                  ? '¡Tarjeta Vecino vigente de la persona a inscribir!'
                  : 'Estado Tarjeta Vecino de la persona a inscribir'}
              </span>
            </div>
          }
          description={
            <div>
              <div className="mb-2">{estadoVecino.mensaje}</div>
              {estadoVecino.esVecino ? (
                <div className="text-xs">
                  Se aplicará automáticamente el <strong>descuento especial para vecinos</strong> en el precio del taller.
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-gray-500">¿Eres vecino y no fue reconocido?</span>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={validando}
                    onClick={revalidar}
                  >
                    Volver a validar
                  </Button>
                </div>
              )}
            </div>
          }
          type={estadoVecino.esVecino ? 'success' : 'info'}
          showIcon
          icon={estadoVecino.esVecino ? <CheckCircleOutlined /> : <IdcardOutlined />}
          className="mb-4"
        />
      )}

      <Radio.Group
        value={tipoInscripcion}
        onChange={(e) => {
          setTipoInscripcion(e.target.value);
          setSelectedFamiliar(null);
          resetEstado();
        }}
        className="mb-4 w-full"
      >
        <Space direction="vertical" className="w-full">
          {/* Opción para mí */}
          <Tooltip
            title={
              !puedeInscribirseSiMismo
                ? "No estás autorizado directamente en esta academia. Solo puedes inscribir a tus familiares autorizados."
                : ""
            }
          >
            <Card
              className={`cursor-pointer ${!puedeInscribirseSiMismo
                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                : tipoInscripcion === 'para_mi'
                  ? 'border-blue-500 bg-blue-50'
                  : ''
                }`}
              onClick={() => {
                if (puedeInscribirseSiMismo) {
                  setTipoInscripcion('para_mi');
                  setSelectedFamiliar(null);
                  resetEstado();
                }
              }}
            >
              <Radio value="para_mi" disabled={!puedeInscribirseSiMismo}>
                <Space>
                  {!puedeInscribirseSiMismo && <LockOutlined className="text-gray-400" />}
                  <UserOutlined />
                  <span className="font-semibold">Para mí</span>
                  {tipoInscripcion === 'para_mi' && esVecino && (
                    <Badge
                      count={<CheckCircleOutlined style={{ color: '#0047bb' }} />}
                      title="Vecino vigente"
                    />
                  )}
                  {!puedeInscribirseSiMismo && (
                    <Tag color="red" size="small">No autorizado</Tag>
                  )}
                </Space>
              </Radio>
            </Card>
          </Tooltip>

          {/* Opción para familiar */}
          <Card
            className={`cursor-pointer ${tipoInscripcion === 'para_familiar' ? 'border-blue-500 bg-blue-50' : ''}`}
            onClick={() => {
              setTipoInscripcion('para_familiar');
              setSelectedFamiliar(null);
              resetEstado();
            }}
          >
            <Radio value="para_familiar">
              <Space>
                <TeamOutlined />
                <span className="font-semibold">Para un familiar</span>
                {tipoInscripcion === 'para_familiar' && esVecino && (
                  <Badge
                    count={<CheckCircleOutlined style={{ color: '#0047bb' }} />}
                    title="Vecino vigente"
                  />
                )}
                {taller?.tipoTaller === 'academia' && tipoAcceso === 'familiar' && (
                  <Tag color="blue" size="small">
                    {familiaresDisponibles.length} disponible(s)
                  </Tag>
                )}
              </Space>
            </Radio>
          </Card>
        </Space>
      </Radio.Group>

      {tipoInscripcion === 'para_mi' ? (
        <div>
          {(() => {
            const validacion = validarRangoEdad(user?.birthdate);
            const categoria = getCategoriaEdad(calcularEdad, user?.birthdate);

            return (
              <>
                {!validacion.valido && (
                  <Alert
                    message="Edad fuera del rango permitido"
                    description={validacion.mensaje}
                    type="error"
                    showIcon
                    icon={<WarningOutlined />}
                    className="mb-4"
                  />
                )}

                <Card className={!validacion.valido ? 'mb-4 border-red-300' : 'mb-4 bg-blue-50 border-blue-200'}>
                  <div className="flex justify-between items-start mb-3">
                    <Alert
                      message="Datos de inscripción personal"
                      description="Se utilizarán los datos de tu cuenta"
                      type="success"
                      showIcon
                      className="flex-1"
                      banner={false}
                    />
                    {esVecino && (
                      <Tag color="blue" icon={<IdcardOutlined />} className="ml-2">
                        VECINO
                      </Tag>
                    )}
                  </div>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nombre">
                      <strong>{user.name}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="RUT">
                      <Space>
                        {user.rut}
                        {validando && <Spin size="small" />}
                        {esVecino && (
                          <Tooltip title="Tarjeta vecino vigente - Se aplicará descuento especial si está disponible">
                            <CheckCircleOutlined style={{ color: '#0047bb', fontSize: '16px' }} />
                          </Tooltip>
                        )}
                      </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      {user.email}
                    </Descriptions.Item>
                    <Descriptions.Item label="Teléfono">
                      {user.phone}
                    </Descriptions.Item>
                    {user.birthdate && (
                      <>
                        <Descriptions.Item label="Fecha de nacimiento">
                          {dayjs(user.birthdate).format('DD/MM/YYYY')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Edad">
                          <Space>
                            <span>{calcularEdad(user.birthdate)} años</span>
                            <Tag color={categoria.color}>{categoria.texto}</Tag>
                          </Space>
                        </Descriptions.Item>
                      </>
                    )}
                  </Descriptions>
                </Card>
              </>
            );
          })()}
        </div>
      ) : (
        <div>
          {loadingFamiliares ? (
            <div className="text-center py-8">
              <Spin />
              <p className="mt-2 text-gray-500">Cargando familiares...</p>
            </div>
          ) : (
            <>
              {familiaresDisponibles.length > 0 ? (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold">
                      {taller?.tipoTaller === 'academia' && tipoAcceso === 'familiar'
                        ? 'Selecciona un familiar autorizado:'
                        : 'Selecciona un familiar registrado:'}
                    </span>
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => navigate('/familiares')}
                    >
                      Gestionar familiares
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md-grid-cols-2 gap-3">
                    {familiaresDisponibles.map((familiar) => {
                      const edad = calcularEdad(familiar.fechaNacimiento);
                      const categoria = getCategoriaEdad(calcularEdad, familiar.fechaNacimiento);
                      const validacion = validarRangoEdad(familiar.fechaNacimiento);
                      const isSelected = selectedFamiliar === familiar._id;
                      const esVecinoFamiliar = isSelected && esVecino;

                      return (
                        <Card
                          key={familiar._id}
                          size="small"
                          className={`cursor-pointer transition-all ${isSelected
                            ? 'border-2 border-blue-500 bg-blue-50'
                            : 'hover:border-blue-300'
                            } ${!validacion.valido ? 'border-red-300 opacity-60' : ''}`}
                          onClick={() => validacion.valido && setSelectedFamiliar(familiar._id)}
                        >
                          <div className="flex items-start gap-3">
                            <Radio checked={isSelected} disabled={!validacion.valido} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{familiar.nombre}</span>
                                {isSelected && validando && <Spin size="small" />}
                                {esVecinoFamiliar && (
                                  <Tooltip title="Tarjeta vecino vigente">
                                    <Tag color="blue" size="small" icon={<IdcardOutlined />}>
                                      VECINO
                                    </Tag>
                                  </Tooltip>
                                )}
                                {taller?.tipoTaller === 'academia' && tipoAcceso === 'familiar' && (
                                  <Tag color="gold" size="small" icon={<CheckCircleOutlined />}>
                                    Autorizado
                                  </Tag>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                <Tag color="blue" size="small">{familiar.rut}</Tag>
                                <Tag color="blue" size="small">
                                  {edad} {edad === 1 ? 'año' : 'años'}
                                </Tag>
                                <Tag color={categoria.color} size="small">
                                  {categoria.texto}
                                </Tag>
                              </div>
                              {familiar.genero && (
                                <div className="text-xs text-gray-500 mt-1 capitalize">
                                  {familiar.genero}
                                </div>
                              )}
                              {!validacion.valido && (
                                <Alert
                                  message={validacion.mensaje}
                                  type="error"
                                  showIcon
                                  icon={<WarningOutlined />}
                                  className="mt-2"
                                  banner
                                />
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div>
                      {taller?.tipoTaller === 'academia' && tipoAcceso === 'familiar' ? (
                        <>
                          <p className="text-gray-500 mb-2">
                            No tienes familiares autorizados para esta academia
                          </p>
                          <p className="text-xs text-gray-400 mb-3">
                            Solo los familiares específicamente autorizados pueden inscribirse en esta academia
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-gray-500 mb-2">No tienes familiares registrados</p>
                          <p className="text-xs text-gray-400 mb-3">
                            Debes registrar un familiar antes de poder inscribirlo en un taller
                          </p>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/familiares')}
                            size="large"
                          >
                            Ir a Familiares
                          </Button>
                        </>
                      )}
                    </div>
                  }
                  className="py-8"
                />
              )}
            </>
          )}
        </div>
      )}

      {yaInscritoEn.length > 0 && (
        <Alert
          message={
            yaInscritoEn.length === 1
              ? `${participante.nombre} ya está inscrito en este horario`
              : `${participante.nombre} ya está inscrito en estos horarios`
          }
          description={
            <>
              <div className="mb-2">
                {yaInscritoEn.map(({ horario, taller: nombreTaller, fechaInscripcion }) => (
                  <div key={horario._id}>
                    · {nombreTaller || taller?.nombre || 'Taller'} {nombreHorario(horario)}
                    {fechaInscripcion && ` — inscrito el ${dayjs(fechaInscripcion).format('DD/MM/YYYY')}`}
                  </div>
                ))}
              </div>
              <div>
                No hace falta volver a pagarlo. Si quieres inscribir a otra persona,
                elígela arriba; si crees que es un error, revisa <strong>Mis talleres</strong>.
              </div>
            </>
          }
          type="error"
          showIcon
          icon={<WarningOutlined />}
          className="mb-4"
          action={
            <Button size="small" onClick={() => navigate('/mis-talleres')}>
              Ver mis talleres
            </Button>
          }
        />
      )}

      <Divider />

      <div className="flex justify-between">
        <Button onClick={onPrev} size="large">
          Anterior
        </Button>
        <Button
          onClick={onNext}
          type="primary"
          size="large"
          loading={verificandoInscripcion}
          disabled={
            // Ya inscrito = no puede seguir: es exactamente el cobro que hay que evitar.
            yaInscritoEn.length > 0 ||
            verificandoInscripcion ||
            (tipoInscripcion === 'para_mi'
              ? !validarRangoEdad(user?.birthdate).valido || !puedeInscribirseSiMismo
              : tipoInscripcion === 'para_familiar'
                ? !selectedFamiliar || (
                  selectedFamiliar &&
                  !validarRangoEdad(
                    familiares.find(f => f._id === selectedFamiliar)?.fechaNacimiento
                  ).valido
                )
                : true)
          }
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}
